import { useEffect, useMemo, useState } from 'react';
import { createHost, type Host, type HostEvent } from './host';

/**
 * React glue for the host bridge. Returns a stable `host` reference plus
 * a snapshot of every host-controlled value that the widget cares about,
 * re-rendering whenever the host pushes a new one.
 *
 * Pass `seedData` only for demo / dev — in production the host injects data.
 */
export function useHost(seedData?: unknown) {
  const host = useMemo<Host>(() => createHost(seedData), [seedData]);
  const [data, setData] = useState(host.data);
  const [widgetState, setWidgetState] = useState(host.widgetState);
  const [theme, setTheme] = useState(host.theme);
  const [locale, setLocale] = useState(host.locale);
  const [displayMode, setDisplayMode] = useState(host.displayMode);

  useEffect(() => {
    const subs: Array<() => void> = [];
    const wire = <E extends HostEvent>(e: E, setter: (v: unknown) => void) => subs.push(host.subscribe(e, setter));
    wire('data', v => setData(v));
    wire('widgetState', v => setWidgetState(v));
    wire('theme', v => setTheme(v as 'light' | 'dark'));
    wire('locale', v => setLocale(v as string));
    wire('displayMode', v => setDisplayMode(v as 'inline' | 'pip' | 'fullscreen'));
    return () => subs.forEach(fn => fn());
  }, [host]);

  return { host, data, widgetState, theme, locale, displayMode };
}
